import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import {
  AccountIcon,
  CommentFavoriteIcon,
  PackageIcon,
  RefundIcon,
} from '@shipt/design-system-icons'
import { screenSizes } from '@shipt/design-system-themes'
import { Body } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { useState } from 'react'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView' // adjust if needed
import { keyframes } from '@/features/shared/styles/effects'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { Box, Column, Stack } from '@shipt/design-system-layouts'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { theme } from '@/features/shared/constants/shiptTheme'

type PointItem = {
  Icon: React.ElementType
  title: string
  description: string
}

type PointsListProps = {
  points: PointItem[]
}

export const C360SavingsPoints = ({
  content_type_id,
  id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const pointsData = [
    {
      Icon: RefundIcon,
      title: 'Don’t let markups spoil the fun',
      description: 'Shop your favorite products with in-store prices. Some exceptions apply.*',
    },
    {
      Icon: PackageIcon,
      title: '$0 delivery fees',
      description: 'Skip the fees on orders over $35.',
    },
    {
      Icon: AccountIcon,
      title: 'Members save more',
      description: 'Unlock exclusive Shipt-only deals on products you love.',
    },
  ]

  return (
    <SectionContainer {...getCMSWrapperProps({ content_type_id, id })} as="section">
      <PointsList points={pointsData} />
    </SectionContainer>
  )
}

export const UngatedHomepageSavingsPoints = ({
  content_type_id,
  id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const pointsData = [
    {
      Icon: PackageIcon,
      title: 'Shop from 120+ stores',
      description: 'Order groceries and more from the local and national stores you already love.',
    },
    {
      Icon: RefundIcon,
      title: 'Save more as a member',
      description: 'Sign up to unlock exclusive deals, $0 delivery fees on $35+ orders¹, and more.',
    },
    {
      Icon: CommentFavoriteIcon,
      title: 'Text with your shopper',
      description: 'Get real-time updates from your shopper and make special requests.',
    },
  ]

  return (
    <SectionContainer {...getCMSWrapperProps({ content_type_id, id })} as="section">
      <PointsList points={pointsData} />
    </SectionContainer>
  )
}

const PointsList = ({ points }: PointsListProps) => {
  const [hasAnimated, setHasAnimated] = useState(false)
  const { trackCMSElementViewed } = useCMSTrackingEvents()

  return (
    <ScrollIntoView
      onInView={() => {
        setHasAnimated(true)
        trackCMSElementViewed({
          content: 'More perks in your pocket points',
        })
      }}
    >
      <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 'xxl', md: 'sm' }}>
        {points.map(({ Icon, title, description }, index) => (
          <Stack
            key={title}
            direction={{ base: 'row', md: 'column' }}
            spacing={{ base: 'lg', md: 'xl' }}
            flexBasis="100%"
          >
            <IconWrapper data-animate={hasAnimated} $delay={index * 0.3}>
              <Icon size="lg" />
            </IconWrapper>
            <TextWrapper data-animate={hasAnimated} $delay={index * 0.3 + 0.15} spacing="xs">
              <Body size="xl" weight="medium">
                {title}
              </Body>
              <Body size="xl">{description}</Body>
            </TextWrapper>
          </Stack>
        ))}
      </Stack>
    </ScrollIntoView>
  )
}

const SectionContainer = styled(GridLayoutContainer)`
  --content-max-width: 1280px;
`

interface AnimatedProps {
  'data-animate': boolean
  $delay: number
}

const IconWrapper = styled(Box)<AnimatedProps>`
  opacity: 0;
  transform: translateY(48px);

  svg {
    color: ${theme.color.content.base.neutral.primary};
    display: block;
  }

  @media ${screenSizes.lgMobile} {
    svg {
      width: 32px;
      height: 32px;
    }
  }

  @media ${screenSizes.max_smMobile} {
    svg {
      width: 24px;
      height: 24px;
    }
  }

  &[data-animate='true'] {
    --enter-y: 48px;
    animation: ${keyframes.enter} 0.4s ease forwards;
    animation-delay: ${({ $delay }) => $delay}s;
  }

  @media ${screenSizes.max_smMobile} {
    transform: translateY(0);
  }
`

const TextWrapper = styled(Column)<AnimatedProps>`
  opacity: 0;

  &[data-animate='true'] {
    animation: ${keyframes.fade} 0.4s ease forwards;
    animation-delay: ${({ $delay }) => $delay}s;
  }

  @media ${screenSizes.tablet} {
    padding-right: 25%;
  }
`
